/*
 * Copyright Mimic Networks, Inc. 2025.
 */

import { Divider } from 'antd';
import { useMemo } from 'react';

import { useMimicTranslation } from '@/hooks/useMimicTranslation';
import { Card } from '@/primitives/Card';
import { Flex } from '@/primitives/Flex';
import { Text } from '@/primitives/Text';

import { ACLProtectionSection } from './Config/ACLProtectionSection';
import { EnginesSection } from './Config/EnginesSection';
import { GeneralConfigSection } from './Config/GeneralConfigSection';
import { parseConfigSource } from './Config/utils/configParser';
import { configOverviewTitleStyle, emptyStateCardStyle, previewTextStyle } from './Config/utils/styles';
import { ConfigPrettyViewerProps } from './Config/utils/types';

export function ConfigPrettyViewer({ source }: ConfigPrettyViewerProps) {
  const { t } = useMimicTranslation('configPrettyViewer');

  const parsedConfig = useMemo(() => parseConfigSource(source || ''), [source]);

  if (!source) {
    return (
      <Card style={emptyStateCardStyle}>
        <Text type="subtle">{t('noConfigurationData')}</Text>
      </Card>
    );
  }

  const hasStructuredData = parsedConfig.aclProtection || parsedConfig.engines || parsedConfig.general;

  if (!hasStructuredData) {
    return (
      <Card style={emptyStateCardStyle}>
        <Flex vertical gap="md" align="center">
          <Text type="subtle">{t('unableToParseStructure')}</Text>
          <Text size="sm" type="subtle">
            {t('unrecognizableContent')}
          </Text>
          <Divider />
          <Text style={previewTextStyle}>{t('rawContentPreview', { preview: source.substring(0, 100) })}</Text>
        </Flex>
      </Card>
    );
  }

  return (
    <div style={{ width: '100%' }}>
      <div style={{ marginBottom: '24px' }}>
        <Text style={configOverviewTitleStyle}>{t('configurationOverview')}</Text>
      </div>

      <div>
        {parsedConfig.aclProtection && <ACLProtectionSection aclProtection={parsedConfig.aclProtection} />}
        {parsedConfig.engines && <EnginesSection engines={parsedConfig.engines} />}
        {parsedConfig.general && <GeneralConfigSection general={parsedConfig.general} />}
      </div>
    </div>
  );
}
